/**
 * The shape the public pages share: a head, then sections of prose, cards or a
 * table. Small on purpose — these are layout primitives, not a component
 * library, and a page should still be readable as a page.
 */

import type { ReactNode } from 'react';
import './Page.scss';

export function PageHead({
  kicker,
  title,
  lead,
}: {
  kicker: string;
  title: string;
  lead: string;
}) {
  return (
    <header className="site-page__head">
      <span className="site-label">{kicker}</span>
      <h1>{title}</h1>
      <p>{lead}</p>
    </header>
  );
}

export function Section({
  id,
  title,
  lead,
  children,
}: {
  /** Set when a hash link needs to reach this section. */
  id?: string;
  title?: string;
  lead?: string;
  children?: ReactNode;
}) {
  return (
    <section className="site-page__section" id={id}>
      {title && <h2>{title}</h2>}
      {lead && <p className="site-page__lead">{lead}</p>}
      {children}
    </section>
  );
}

export function CardGrid({ children, min = 280 }: { children: ReactNode; min?: number }) {
  return (
    <div
      className="site-page__grid"
      style={{ '--min': `${min}px` } as React.CSSProperties}
    >
      {children}
    </div>
  );
}

export function Card({
  title,
  children,
  tone = 'plain',
}: {
  title: string;
  children: ReactNode;
  /** `inverse` is the dark block used where a card should read as a counterweight. */
  tone?: 'plain' | 'inverse';
}) {
  return (
    <article className={`site-page__card site-page__card--${tone}`}>
      <h3>{title}</h3>
      {children}
    </article>
  );
}

/**
 * A table whose first column is a label. `head` may be shorter than a row when
 * the first column has no useful heading, which is common here.
 */
export function Table({
  head,
  rows,
  widths = '1fr 1.5fr',
}: {
  head: string[];
  rows: ReactNode[][];
  widths?: string;
}) {
  return (
    <div className="site-page__table" role="table">
      <div className="site-page__tr site-page__tr--head" role="row" style={{ gridTemplateColumns: widths }}>
        {head.map((h, i) => (
          <span key={i} className="site-label" role="columnheader">
            {h}
          </span>
        ))}
      </div>
      {rows.map((row, i) => (
        <div key={i} className="site-page__tr" role="row" style={{ gridTemplateColumns: widths }}>
          {row.map((cell, j) => (
            <span key={j} role="cell" className={j === 0 ? 'site-page__th' : undefined}>
              {cell}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}
